import { useState, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

const GoogleAutoComplete = ({ handlePlaceSelect }) => {
	const [inputValue, setInputValue] = useState("");
	const [suggestions, setSuggestions] = useState([]);
	const [sessionToken, setSessionToken] = useState(uuidv4());
	const debounceTimerRef = useRef(null);
	const apiKey = process.env.NEXT_PUBLIC_OPENCAGE_API_KEY; // Ensure this is the correct environment variable

	// Fetch autocomplete predictions
	const fetchPredictions = async (input) => {
		if (!input) return;

		try {
			
			const response = await fetch(
				"https://places.googleapis.com/v1/places:autocomplete", // Ensure this is the correct endpoint
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-Goog-Api-Key": apiKey,
						"X-Goog-FieldMask":
							"suggestions.placePrediction.placeId," +
							"suggestions.placePrediction.structuredFormat.mainText.text," +
							"suggestions.placePrediction.structuredFormat.secondaryText.text",
					},
					body: JSON.stringify({
						input: input,
						sessionToken: sessionToken,
						regionCode: "NG",
						locationRestriction: {
							rectangle: {
							  low: {
							    latitude: 4.0,
							    longitude: 2.7,
							  },
							  high: {
							    latitude: 13.8,
							    longitude: 14.6,
							  },
							},
						}
					}),
				}
			);

			const data = await response.json();
			
			setSuggestions(data.suggestions || []);
		} catch (error) {
			console.error("Error fetching predictions:", error);
		}
	};

	// Fetch full place details
	const fetchPlaceDetails = async (placeId) => {
		try {
			const response = await fetch(
				`https://places.googleapis.com/v1/places/${placeId}?sessionToken=${sessionToken}`,
				{
					method: "GET",
					headers: {
						"X-Goog-Api-Key": apiKey,
						"X-Goog-FieldMask": "id,displayName,formattedAddress,location",
					},
				}
			);

			const data = await response.json();
			
			handlePlaceSelect(data);
			setInputValue(data.formattedAddress || data.displayName?.text || "");
			setSuggestions([]);
			setSessionToken(uuidv4()); // Create a new session for next time
		} catch (error) {
			console.error("Error fetching place details:", error);
		}
	};

	const handleInputChange = (e) => {
		const value = e.target.value;
		
		setInputValue(value);

		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		if (value.length >= 3) {
			debounceTimerRef.current = setTimeout(() => {
				fetchPredictions(value);
			}, 500); // Debounce typing by 500ms
		} else {
			setSuggestions([]);
		}
	};

	const handleSuggestionClick = (suggestion) => {
		fetchPlaceDetails(suggestion.placePrediction.placeId);
	};

	return (
		<div style={{ width: "100%", position: "relative" }}>
			<style jsx>{`
				.custom-placeholder::placeholder {
					font-size: 16px;
					padding-left: 10px;
				}
				:global(.form-control:focus) {
					outline: none !important;
					box-shadow: none !important;
					border-color: #ced4da !important; /* Same as Bootstrap's default border color */
				}
			`}</style>
			<input
				type="text"
				className="form-control custom-placeholder "
				style={{
					width: "100%",
					height: "40px",
					outline: "none",
					boxShadow: "none",
					fontSize: "18px",
					borderRadius: "25px",
					border: " 1px solid #d9d9d",
					background: "#FFFFF"
				}}
				placeholder="Your address"
				value={inputValue}
				onChange={handleInputChange}
			/>
			{suggestions.length > 0 && (
				<div
					style={{
						backgroundColor: "white",
						border: "1px solid #ccc",
						borderRadius: "4px",
						marginTop: "5px",
						maxHeight: "200px",
						overflowY: "auto",
						position: "absolute",
						width: "100%",
						zIndex: 1000,
					}}>
					{suggestions.map((suggestion) => (
						<div
							key={suggestion.placePrediction.placeId}
							onClick={() => handleSuggestionClick(suggestion)}
							style={{
								padding: "10px",
								cursor: "pointer",
								borderBottom: "1px solid #eee",
							}}>
							{suggestion.placePrediction.structuredFormat.mainText.text},
							{suggestion.placePrediction.structuredFormat.secondaryText?.text}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default GoogleAutoComplete;
